import {produce} from 'immer';
import {WritableDraft} from 'immer/src/types/types-external';
import {create} from 'zustand';
import {immer} from 'zustand/middleware/immer';
import translate from '@/utils/translate';
import Storage from '@/utils/wrappers/Storage';
import {COLUMN_ID} from '@/types/models/creator-crm/columnId';
import type {AgeRange} from '@/types/models/creator-crm/creatorCrm';
import {
  CreatorSearchResult,
  Metadata,
  SearchState,
} from '@/types/models/creator-crm/creatorCrm';
import {
  CreatorCrmStateDefinition,
  TableColumnConfig,
  ViewType,
} from '@/types/models/creator-crm/creatorCrmStore';
import {
  AudienceAgeSelection,
  BaseFilterOption,
  DynamicPlatformFilters,
  Filter,
  FiltersGroup,
  FiltersState,
  MultiSelection,
  MultiSelectionAsync,
  MultiUserInput,
  Range,
  RangeSelection,
  SingleSelection,
  SingleUserInput,
  WeightedFilterOption,
  WeightedMultiSelection,
} from '@/types/models/creator-crm/filter';
import {
  FilterIds,
  FilterOptionSelection,
  FiltersGroupId,
  FILTERS_GROUPS_IDS,
  PlatformFilterId,
  PlatformFilterIds,
  PlatformId,
  UpdatableFilterOptionSelection,
} from '@/types/models/creator-crm/filterId';
import {
  FilterUpdate,
  LdaUpdate,
  WeightUpdate,
} from '@/types/models/creator-crm/filterUpdate';
import {
  DefaultSortDirection,
  DefaultSortField,
} from '@/types/models/creator-crm/sortField';
import {SortDirection} from '@/types/models/creator-crm/sorting';
import {
  CREATOR_CRM_ITEMS_PER_PAGE,
  VIEW_TYPE_GRID,
  VIEW_TYPE_TABLE,
  initialPaging,
} from '@/stores/creator-crm/constants';
import initialFiltersGroups from '@/stores/creator-crm/filterGroupInstance';
import {
  findExpandedFilter,
  getSortFieldFromString,
  getDirectOrNestedFilter,
  hasSelectedFilters,
  isFiltersGroupChanged,
  resetFilterDrawerState,
  resetSelected,
  resetSessionInformation,
  updateAsyncInitializableFilterOptions,
  updateLda,
  updateWeight,
  setUpdatedFilter,
} from '@/stores/creator-crm/utils';

// Re-export for backward compatibility
export {
  CREATOR_CRM_ITEMS_PER_PAGE,
  VIEW_TYPE_GRID,
  VIEW_TYPE_TABLE,
  initialPaging,
};

type AppliedFilters = Partial<Record<FilterIds, unknown>>;
export type AppliedFiltersGroups = Partial<
  Record<FiltersGroupId, AppliedFilters>
>;

function ageRangeMatches(a: AgeRange, b: AgeRange): boolean {
  const aFrom = 'fromAge' in a ? a.fromAge : undefined;
  const aTo = 'toAge' in a ? a.toAge : undefined;
  const bFrom = 'fromAge' in b ? b.fromAge : undefined;
  const bTo = 'toAge' in b ? b.toAge : undefined;
  return aFrom === bFrom && aTo === bTo;
}

function selectAppliedFilter(
  filter: WritableDraft<Filter>,
  appliedFilter: unknown
): void {
  switch (filter.type) {
    case 'singleSelection': {
      const ssf = filter as WritableDraft<SingleSelection<BaseFilterOption>>;
      if (ssf.asyncInitializationKey) {
        ssf.selected = appliedFilter as BaseFilterOption;
      } else {
        let selectedOption = ssf.options.find(
          (option) => option.id === (appliedFilter as string)
        );
        if (
          selectedOption === undefined &&
          typeof appliedFilter === 'number' &&
          ssf.options.length > 0
        ) {
          selectedOption = ssf.options.find(
            (option) =>
              option.id === `${appliedFilter}%` ||
              option.value === appliedFilter / 100 ||
              option.value === appliedFilter
          );
        }
        if (selectedOption) {
          ssf.selected = selectedOption;
        }
      }
      break;
    }
    case 'weightedSingleSelection': {
      const wssf = filter as WritableDraft<
        SingleSelection<WeightedFilterOption>
      >;
      if (wssf.asyncInitializationKey) {
        wssf.selected = appliedFilter as BaseFilterOption;
      } else {
        const appliedFilterOptionIdAndWeight = appliedFilter as [
          string,
          number | undefined,
        ];
        const selectedOption = wssf.options.find(
          (option) => option.id === appliedFilterOptionIdAndWeight[0]
        );
        if (selectedOption) {
          wssf.selected = {
            ...selectedOption,
            weight: appliedFilterOptionIdAndWeight[1],
          };
        }
      }
      break;
    }
    case 'multiSelection': {
      const msf = filter as WritableDraft<MultiSelection<BaseFilterOption>>;
      if (msf.asyncInitializationKey) {
        const applied = appliedFilter as unknown;
        if (
          Array.isArray(applied) &&
          applied.length > 0 &&
          typeof applied[0] === 'string'
        ) {
          msf.selected = msf.options.filter((opt) =>
            (applied as string[]).includes(opt.id)
          );
        } else {
          msf.selected = appliedFilter as BaseFilterOption[];
        }
      } else {
        const appliedFilterOptionIds = appliedFilter as string[];
        msf.selected = msf.options.filter((option) =>
          appliedFilterOptionIds.includes(option.id)
        );
      }
      break;
    }
    case 'multiSelectionAsync': {
      const msa = filter as WritableDraft<
        MultiSelectionAsync<BaseFilterOption>
      >;
      msa.selected = appliedFilter as BaseFilterOption[];
      break;
    }
    case 'weightedMultiSelection': {
      const wmsf = filter as WritableDraft<
        WeightedMultiSelection<WeightedFilterOption>
      >;
      if (wmsf.asyncInitializationKey) {
        wmsf.selected = appliedFilter as WeightedFilterOption[];
      } else {
        const appliedFilterOptionIdsAndWeightsMap = new Map(
          appliedFilter as [string, number | undefined][]
        );
        wmsf.selected = wmsf.options
          .filter((option) =>
            appliedFilterOptionIdsAndWeightsMap.has(option.id)
          )
          .map((option) => ({
            ...option,
            weight: appliedFilterOptionIdsAndWeightsMap.get(option.id),
          }));
      }
      break;
    }
    case 'audienceAge': {
      const aas = filter as WritableDraft<AudienceAgeSelection>;
      if (typeof appliedFilter === 'boolean') {
        aas.ldaSelected = appliedFilter;
      } else {
        const applied = appliedFilter as unknown[];
        const isValueWeightPairs =
          Array.isArray(applied) &&
          applied.length > 0 &&
          Array.isArray(applied[0]) &&
          applied[0].length >= 1 &&
          typeof applied[0][0] === 'object' &&
          applied[0][0] !== null &&
          ('fromAge' in (applied[0][0] as object) ||
            'toAge' in (applied[0][0] as object));
        if (isValueWeightPairs) {
          const pairs = applied as [AgeRange, number | undefined][];
          aas.selected = pairs
            .map(([value, weight]) => {
              const option = aas.options.find((opt) =>
                ageRangeMatches(opt.value as AgeRange, value as AgeRange)
              );
              return option ? {...option, weight} : null;
            })
            .filter(Boolean) as (BaseFilterOption & {weight?: number})[];
        } else {
          const appliedFilterOptionIdsAndWeightsMap = new Map(
            appliedFilter as [string, number | undefined][]
          );
          aas.selected = aas.options
            .filter((option) =>
              appliedFilterOptionIdsAndWeightsMap.has(option.id)
            )
            .map((option) => ({
              ...option,
              weight: appliedFilterOptionIdsAndWeightsMap.get(option.id),
            }));
        }
      }
      break;
    }
    case 'rangeSelection': {
      const rs = filter as WritableDraft<RangeSelection>;
      rs.selected = appliedFilter as Range;
      break;
    }
    case 'multiUserInput': {
      const mui = filter as MultiUserInput;
      mui.inputs = appliedFilter as string[];
      break;
    }
    case 'singleUserInput': {
      const sui = filter as SingleUserInput;
      sui.input = appliedFilter as string;
      break;
    }
    case 'dynamicPlatformFilters': {
      const dpf = filter as WritableDraft<DynamicPlatformFilters>;
      const [platformId, appliedPlatformFilters] = appliedFilter as [
        PlatformId,
        Partial<Record<string, unknown>>,
      ];
      const selectedPlatformFilters = dpf.platformsFilters[platformId];
      dpf.platformId = platformId;

      Object.entries(appliedPlatformFilters || {}).forEach(
        ([platformFilterId, appliedPlatformFilter]) => {
          const platformFilter =
            selectedPlatformFilters?.[platformFilterId as PlatformFilterIds];
          if (!platformFilter) return;
          selectAppliedFilter(platformFilter, appliedPlatformFilter);
        }
      );
      break;
    }
    default:
  }
}

/**
 * Applies applied filters and optional search state to a CRM draft.
 * Used when loading a segment or restoring from persistence (URL/storage).
 */
export function applyAppliedFiltersToDraft(
  draft: WritableDraft<CreatorCrmStateDefinition>,
  appliedFiltersGroups: AppliedFiltersGroups,
  searchState?: SearchState
): void {
  if (searchState) {
    draft.content.filtersState.searchState = searchState;
  }
  const filterGroups = draft.content.filtersState
    .filtersGroupsSnapshot as Record<FiltersGroupId, FiltersGroup<FilterIds>>;
  Object.keys(appliedFiltersGroups).forEach((groupId) => {
    const {filters} = filterGroups[groupId as FiltersGroupId];
    const appliedFilters = appliedFiltersGroups[groupId as FiltersGroupId];
    if (!appliedFilters) return;
    Object.keys(appliedFilters).forEach((filterId) => {
      const filter = filters[filterId as FilterIds];
      const appliedFilter = appliedFilters[filterId as FilterIds];
      if (!filter || !appliedFilter) {
        return;
      }
      selectAppliedFilter(filter, appliedFilter);
      if (groupId === 'platforms' && filterId === 'platform') {
        const platformMetrics = filterGroups.platforms.filters
          .platformMetrics as WritableDraft<DynamicPlatformFilters>;
        platformMetrics.platformId = appliedFilter as PlatformId;
        const platform = filterGroups.platforms.filters
          .platform as WritableDraft<SingleSelection<BaseFilterOption>>;
        const isIrrelevant = platform.selected?.id !== 'instagram';
        const platformFilters = filterGroups.platforms.filters as WritableDraft<
          Record<PlatformFilterId, Filter>
        >;
        platformFilters.age.isIrrelevant = isIrrelevant;
        platformFilters.gender.isIrrelevant = isIrrelevant;
        platformFilters.location.isIrrelevant = isIrrelevant;
      }
    });
  });
  draft.content.filtersState.filtersGroups = filterGroups;
}

const STORAGE_SORT_KEY = 'creatorCrmSort';

const TRANSLATION_PREFIX = 'views.creators.components.table.columns';

const INITIAL_TABLE_COLUMNS: TableColumnConfig[] = [
  {
    id: COLUMN_ID.name,
    localeLabelKey: `${TRANSLATION_PREFIX}.name`,
    visible: true,
    pinned: false,
    sortable: false,
    locked: false,
    canPin: false,
  },
  {
    id: COLUMN_ID.followers,
    localeLabelKey: `${TRANSLATION_PREFIX}.followers`,
    visible: true,
    pinned: false,
    sortable: true,
    locked: false,
    canPin: true,
  },
  {
    id: COLUMN_ID.engagementRate,
    localeLabelKey: `${TRANSLATION_PREFIX}.engagement-rate`,
    visible: true,
    pinned: false,
    sortable: true,
    locked: false,
    canPin: true,
  },
  {
    id: COLUMN_ID.location,
    localeLabelKey: 'Location',
    visible: false,
    pinned: false,
    sortable: false,
    locked: false,
    canPin: true,
  },
  {
    id: COLUMN_ID.joined,
    localeLabelKey: 'Joined',
    visible: false,
    pinned: false,
    sortable: false,
    locked: false,
    canPin: true,
  },
  {
    id: COLUMN_ID.gender,
    localeLabelKey: 'Gender',
    visible: false,
    pinned: false,
    sortable: false,
    locked: false,
    canPin: true,
  },
  {
    id: COLUMN_ID.age,
    localeLabelKey: 'Age',
    visible: false,
    pinned: false,
    sortable: false,
    locked: false,
    canPin: true,
  },
  {
    id: COLUMN_ID.categories,
    localeLabelKey: 'Categories',
    visible: false,
    pinned: false,
    sortable: false,
    locked: false,
    canPin: true,
  },
  {
    id: COLUMN_ID.socialNetworks,
    localeLabelKey: 'Platforms',
    visible: true,
    pinned: false,
    sortable: false,
    locked: false,
    canPin: true,
  },
  {
    id: COLUMN_ID.vettingDecision,
    localeLabelKey: 'Safe Collab Status',
    visible: true,
    pinned: false,
    sortable: true,
    locked: false,
    canPin: true,
  },
];

export const initialCreatorCrmState: CreatorCrmStateDefinition = {
  viewType: VIEW_TYPE_TABLE,
  isMetadataLoaded: false,
  isSelectMode: false,
  isCrmMode: true,
  selectedBrandId: '',
  selectedSegmentId: null,
  content: {
    sessionId: undefined,
    paging: initialPaging,
    sorting: Storage.getItemSync(STORAGE_SORT_KEY) || {
      field: DefaultSortField,
      direction: DefaultSortDirection,
    },
    selectedCreators: new Map(),
    isAllFilterMatchingSelected: false,
    filtersState: {
      isFiltersDrawerOpen: false,
      filtersGroups: initialFiltersGroups,
      filtersGroupsSnapshot: initialFiltersGroups,
      searchState: {
        type: 'all',
        chips: [],
      },
    },
    tableColumns: INITIAL_TABLE_COLUMNS.map((column) => ({...column})),
  },
};

const creatorCrmStore = create<CreatorCrmStateDefinition>()(
  immer(() => initialCreatorCrmState)
);

const updateUserLastBrandId = (brandId: string) =>
  creatorCrmStore.setState((draft) => {
    draft.selectedBrandId = brandId;
  });

const updateSearchType = (type: SearchState['type']) => {
  creatorCrmStore.setState((draft) => {
    if (!draft.content.filtersState.searchState) {
      draft.content.filtersState.searchState = {
        type: 'all',
        chips: [],
      };
    }
    draft.content.filtersState.searchState.type = type;
    draft.selectedSegmentId = null;
    // Only trigger search if there are existing chips (changing type with chips should re-search)
    // If no chips, just update the type and clear session to prevent GET requests with old session
    if (draft.content.filtersState.searchState.chips.length > 0) {
      resetSessionInformation(draft.content);
    } else {
      // Clear session to prevent GET requests when changing type without chips
      draft.content.sessionId = undefined;
    }
  });
};

const updateSearchChips = (chips: string[]) => {
  creatorCrmStore.setState((draft) => {
    if (!draft.content.filtersState.searchState) {
      draft.content.filtersState.searchState = {
        type: 'all',
        chips: [],
      };
    }
    draft.content.filtersState.searchState.chips = chips;
    draft.selectedSegmentId = null;
    resetSessionInformation(draft.content);
  });
};

const changePage = (page: number) =>
  creatorCrmStore.setState((draft) => {
    draft.content.paging.page = page;
  });

const setSessionInformation = (sessionId: string) =>
  creatorCrmStore.setState((draft) => {
    draft.content.sessionId = sessionId;
  });

const changeViewType = (viewType: ViewType) =>
  creatorCrmStore.setState(() => ({viewType}));

const selectCreator = (creator: CreatorSearchResult, isSelected: boolean) =>
  creatorCrmStore.setState((draft) => {
    if (isSelected) {
      draft.content.selectedCreators.set(creator.id, creator);
    } else {
      draft.content.selectedCreators.delete(creator.id);
    }
  });

const toggleSelectAllCreatorsOnPage = (
  creators: CreatorSearchResult[],
  isSelected: boolean
) =>
  creatorCrmStore.setState((draft) => {
    if (isSelected) {
      creators.forEach((creator) => {
        draft.content.selectedCreators.set(creator.id, creator);
      });
    } else {
      creators.forEach((creator) => {
        draft.content.selectedCreators.delete(creator.id);
      });
    }
  });

const setIsAllFilterMatchingSelected = (active: boolean) =>
  creatorCrmStore.setState((draft) => {
    draft.content.isAllFilterMatchingSelected = active;
  });

const clearSelectedCreators = () =>
  creatorCrmStore.setState((draft) => {
    draft.content.selectedCreators = new Map();
    draft.content.isAllFilterMatchingSelected = false;
  });

const changeSorting = (
  sortField: string,
  direction: SortDirection = DefaultSortDirection
) =>
  creatorCrmStore.setState((draft) => {
    draft.content.sessionId = undefined;
    draft.content.paging.page = 1;
    draft.content.paging.totalResults = 0;
    const sorting = {
      field: getSortFieldFromString(sortField) ?? DefaultSortField,
      direction,
    };
    draft.content.sorting = sorting;
    draft.content.selectedCreators = new Map();
    draft.content.isAllFilterMatchingSelected = false;
    Storage.setItem(STORAGE_SORT_KEY, sorting);
  });

const openFiltersDrawer = () =>
  creatorCrmStore.setState((draft) => {
    draft.content.filtersState.isFiltersDrawerOpen = true;
    draft.content.filtersState.filtersGroupsSnapshot =
      draft.content.filtersState.filtersGroups;
  });

const closeFiltersDrawer = () =>
  creatorCrmStore.setState((draft) => {
    draft.content.filtersState.isFiltersDrawerOpen = false;
    draft.content.filtersState.filtersGroups =
      draft.content.filtersState.filtersGroupsSnapshot;
  });

const resetFilters = () => {
  creatorCrmStore.setState((draft) => {
    const {filtersGroups} = draft.content.filtersState;
    let hasSelectedFiltersInAnyGroup = false;

    FILTERS_GROUPS_IDS.forEach((groupId) => {
      const {filters} = filtersGroups[groupId];
      const initialFiltersGroup = initialFiltersGroups[groupId];

      filtersGroups[groupId].expandedFilter = undefined;

      if (hasSelectedFilters(filters)) {
        hasSelectedFiltersInAnyGroup = true;
        resetPlatformMetrics(groupId, filters);
        Object.keys(filters).forEach((filterId) => {
          const id = filterId as FilterIds;
          const filter = filters[id];
          const initialFilter = initialFiltersGroup.filters[id];
          if (filter && initialFilter) {
            resetSelected(filter, initialFilter);
          }
        });
      }
    });

    draft.content.filtersState.filtersGroupsSnapshot =
      draft.content.filtersState.filtersGroups;
    draft.selectedSegmentId = null;

    if (hasSelectedFiltersInAnyGroup) {
      resetSessionInformation(draft.content);
    }
  });
};

const resetFiltersAndCloseDrawer = () => {
  const {isFiltersDrawerOpen} = creatorCrmStore.getState().content.filtersState;
  if (!isFiltersDrawerOpen) {
    return;
  }

  updateSearchChips([]);
  resetFilters();
  creatorCrmStore.setState((draft) => {
    draft.content.filtersState.isFiltersDrawerOpen = false;
  });
};

const resetAllFilters = (forceShouldResetSession = false) => {
  creatorCrmStore.setState((draft) => {
    draft.content.filtersState.isFiltersDrawerOpen = false;
    let shouldResetSession = false;
    const {filtersGroups} = draft.content.filtersState;
    Object.keys(filtersGroups).forEach((_filtersGroupId) => {
      const filtersGroupId = _filtersGroupId as FiltersGroupId;
      const initialFiltersGroup = initialFiltersGroups[filtersGroupId];
      const {filters} = filtersGroups[filtersGroupId];
      filtersGroups[filtersGroupId].expandedFilter = undefined;
      resetPlatformMetrics(filtersGroupId, filters);
      if (hasSelectedFilters(filters)) {
        shouldResetSession = true;
        Object.keys(filters).forEach((_filterId) => {
          const filterId = _filterId as FilterIds;
          const filter = filters[filterId];
          const initialFilter = initialFiltersGroup.filters[filterId];
          if (filter && initialFilter) {
            resetSelected(filter, initialFilter);
          }
        });
      }
    });
    draft.selectedSegmentId = null;
    if (shouldResetSession || forceShouldResetSession) {
      resetSessionInformation(draft.content);

      draft.content.filtersState.filtersGroupsSnapshot =
        draft.content.filtersState.filtersGroups;
    }
  });
};

function resetPlatformMetrics(
  filterGroupId: FiltersGroupId,
  filters: WritableDraft<Partial<Record<FilterIds, Filter>>>
) {
  if (filterGroupId === 'platforms') {
    const platformMetrics =
      filters.platformMetrics as WritableDraft<DynamicPlatformFilters>;
    platformMetrics.platformId = (
      initialFiltersGroups[filterGroupId].filters
        .platformMetrics as DynamicPlatformFilters
    ).platformId;
  }
}

export type ApplyFiltersGroupOptions = {
  /** When false, applies filters without closing the drawer. Default true. */
  closeDrawer?: boolean;
  /** When false, applies filters even if drawer is closed. Default true. */
  requireDrawerOpen?: boolean;
};

const applyFiltersGroup = (options?: ApplyFiltersGroupOptions) => {
  const {filtersGroups, filtersGroupsSnapshot, isFiltersDrawerOpen} =
    creatorCrmStore.getState().content.filtersState;

  const requireDrawerOpen = options?.requireDrawerOpen !== false;
  if (requireDrawerOpen && !isFiltersDrawerOpen) {
    return;
  }

  const closeDrawer = options?.closeDrawer !== false;

  const hasChanges = FILTERS_GROUPS_IDS.some((groupId) =>
    isFiltersGroupChanged(
      filtersGroups[groupId].filters,
      filtersGroupsSnapshot[groupId].filters
    )
  );

  creatorCrmStore.setState((draft) => {
    if (closeDrawer) {
      resetFilterDrawerState(draft.content.filtersState);
    }
    if (hasChanges) {
      draft.selectedSegmentId = null;
      resetSessionInformation(draft.content);
      draft.content.filtersState.filtersGroupsSnapshot =
        draft.content.filtersState.filtersGroups;
    }
  });
};

const applyFiltersFromFilterGroups = () => {
  const {filtersGroups} = creatorCrmStore.getState().content.filtersState;
  creatorCrmStore.setState((draft) => {
    draft.content.filtersState.filtersGroupsSnapshot = filtersGroups;
  });
};

/** Sync snapshot from filtersGroups and reset session so search runs immediately (e.g. when changing handle platform). */
const applyFiltersFromFilterGroupsAndResetSession = () => {
  const {filtersGroups} = creatorCrmStore.getState().content.filtersState;
  creatorCrmStore.setState((draft) => {
    draft.content.filtersState.filtersGroupsSnapshot = filtersGroups;
    draft.selectedSegmentId = null;
    resetSessionInformation(draft.content);
  });
};

const applyAppliedFiltersGroups = (
  appliedFiltersGroups: AppliedFiltersGroups,
  searchState?: SearchState
) => {
  creatorCrmStore.setState((draft) => {
    applyAppliedFiltersToDraft(draft, appliedFiltersGroups, searchState);
  });
  applyFiltersFromFilterGroupsAndResetSession();
};

const toggleExpandFilter = (
  filtersGroupId: FiltersGroupId,
  filterId: FilterIds
) => {
  const {filtersState} = creatorCrmStore.getState().content;
  if (!filtersState.isFiltersDrawerOpen) {
    return;
  }
  const filtersGroup = filtersState.filtersGroups[filtersGroupId];
  if (filtersGroup) {
    const isExpanded = filtersGroup.expandedFilter === filterId;
    creatorCrmStore.setState((draft) => {
      draft.content.filtersState.filtersGroups[filtersGroupId].expandedFilter =
        isExpanded ? undefined : filterId;
    });
  }
};

const changeUserInputFilter = (
  filtersGroupId: FiltersGroupId,
  filterId: FilterIds,
  input: string,
  platformFilterId?: PlatformFilterIds,
  onlyIfDrawerOpen = true
) => {
  const {filtersState} = creatorCrmStore.getState().content;
  const filter = findExpandedFilter(
    filtersState,
    'multiUserInput',
    filtersGroupId,
    filterId,
    platformFilterId,
    onlyIfDrawerOpen
  );
  if (typeof filter === 'undefined') {
    console.log('Invalid input - operation is not possible');
    return;
  }
  const inputFilter = filter as MultiUserInput;
  const updatedInputs = inputFilter.inputs.includes(input)
    ? inputFilter.inputs.filter((_input) => _input !== input)
    : [...inputFilter.inputs, input];
  const updatedFilter = {...inputFilter, inputs: updatedInputs};
  setFilter(filtersGroupId, filterId, updatedFilter, platformFilterId);
};

const changeSingleUserInputFilter = (
  filtersGroupId: FiltersGroupId,
  filterId: FilterIds,
  input: string,
  platformFilterId?: PlatformFilterIds,
  onlyIfDrawerOpen = true
) => {
  const {filtersState} = creatorCrmStore.getState().content;

  const filter = findExpandedFilter(
    filtersState,
    'singleUserInput',
    filtersGroupId,
    filterId,
    platformFilterId,
    onlyIfDrawerOpen
  );

  if (typeof filter === 'undefined') {
    console.log('Invalid input - operation is not possible');
    return;
  }
  const inputFilter = filter as SingleUserInput;
  const updatedFilter = {...inputFilter, input} as SingleUserInput;
  setFilter(filtersGroupId, filterId, updatedFilter, platformFilterId);
};

const changeFilterRange = (
  filtersGroupId: FiltersGroupId,
  filterId: FilterIds,
  range?: Range,
  platformFilterId?: PlatformFilterIds
) => {
  const {filtersState} = creatorCrmStore.getState().content;
  const filter = findExpandedFilter(
    filtersState,
    'rangeSelection',
    filtersGroupId,
    filterId,
    platformFilterId
  );

  if (typeof filter === 'undefined') {
    console.log('Invalid input - operation is not possible');
    return;
  }
  const updatedFilter = {...(filter as RangeSelection), selected: range};
  setFilter(filtersGroupId, filterId, updatedFilter, platformFilterId);
};

const changeFilterOptionSelection = (
  filterSelectionType: FilterOptionSelection,
  filtersGroupId: FiltersGroupId,
  filterId: FilterIds,
  option: BaseFilterOption,
  platformFilterId?: PlatformFilterIds
) => {
  const {filtersState} = creatorCrmStore.getState().content;
  const filter = findExpandedFilter(
    filtersState,
    filterSelectionType,
    filtersGroupId,
    filterId,
    platformFilterId
  );

  const updatedFilter = setUpdatedFilter(filter, filterSelectionType, option);
  if (updatedFilter) {
    setFilter(filtersGroupId, filterId, updatedFilter, platformFilterId);
  }
  setPlatformMetricsPlatformId(option, filtersState, filtersGroupId, filterId);
  handleAudienceFiltersRelevance(option, filtersGroupId, filterId);
};

const forceChangeFilterRange = (
  filtersGroupId: FiltersGroupId,
  filterId: FilterIds,
  range?: Range,
  platformFilterId?: PlatformFilterIds
) => {
  const {filtersState} = creatorCrmStore.getState().content;
  const filterGroup = filtersState.filtersGroups[filtersGroupId];
  const filter = getDirectOrNestedFilter(
    filterGroup.filters,
    filterId,
    platformFilterId
  );

  if (typeof filter === 'undefined') {
    console.log('Invalid input - operation is not possible');
    return;
  }
  const updatedFilter = {...(filter as RangeSelection), selected: range};
  setFilter(filtersGroupId, filterId, updatedFilter, platformFilterId);
};

const forceChangeFilterOptionSelection = (
  filterSelectionType: FilterOptionSelection,
  filtersGroupId: FiltersGroupId,
  filterId: FilterIds,
  option: BaseFilterOption,
  platformFilterId?: PlatformFilterIds
) => {
  const {filtersState} = creatorCrmStore.getState().content;
  const filterGroup = filtersState.filtersGroups[filtersGroupId];
  const filter = getDirectOrNestedFilter(
    filterGroup.filters,
    filterId,
    platformFilterId
  );

  const updatedFilter = setUpdatedFilter(filter, filterSelectionType, option);
  if (updatedFilter) {
    setFilter(filtersGroupId, filterId, updatedFilter, platformFilterId);
  }
  if (filtersGroupId === 'platforms' && filterId === 'platform') {
    const stateAfter = creatorCrmStore.getState().content.filtersState;
    const platformFilter = stateAfter.filtersGroups.platforms.filters
      .platform as SingleSelection<BaseFilterOption>;
    if (!platformFilter.selected) {
      resetPlatformMetricsToInitial();
    } else {
      setPlatformMetricsPlatformId(
        option,
        stateAfter,
        filtersGroupId,
        filterId
      );
    }
  } else {
    setPlatformMetricsPlatformId(
      option,
      filtersState,
      filtersGroupId,
      filterId
    );
  }
  handleAudienceFiltersRelevance(option, filtersGroupId, filterId);
};

function resetPlatformMetricsToInitial() {
  const {filtersState} = creatorCrmStore.getState().content;
  const platformMetrics = filtersState.filtersGroups.platforms.filters
    .platformMetrics as DynamicPlatformFilters;
  const initialPlatformId = (
    initialFiltersGroups.platforms.filters
      .platformMetrics as DynamicPlatformFilters
  ).platformId;
  const updatedPlatformMetrics = produce(platformMetrics, (draft) => {
    draft.platformId = initialPlatformId;
  });
  setFilter('platforms', 'platformMetrics', updatedPlatformMetrics);
}

function setPlatformMetricsPlatformId(
  option: BaseFilterOption,
  filtersState: FiltersState,
  filtersGroupId: FiltersGroupId,
  filterId: FilterIds
) {
  if (filtersGroupId !== 'platforms' || filterId !== 'platform') return;
  const platformMetrics = filtersState.filtersGroups.platforms.filters
    .platformMetrics as DynamicPlatformFilters;

  const updatedPlatformMetrics = produce(platformMetrics, (draft) => {
    const oldPlatformFilters =
      platformMetrics.platformsFilters[draft.platformId];
    const newPlatformFilters = draft.platformsFilters[option.id as PlatformId];

    /* TODO: check we have newPlatformFilters */
    if (!newPlatformFilters) return;
    Object.keys(oldPlatformFilters).forEach((id) => {
      const platformFilterId = id as PlatformFilterIds;
      const oldFilter = oldPlatformFilters[platformFilterId];
      const newFilter = newPlatformFilters[platformFilterId];
      if (!oldFilter || !newFilter) return;
      switch (oldFilter.type) {
        case 'singleSelection':
          (newFilter as SingleSelection<BaseFilterOption>).selected = (
            oldFilter as SingleSelection<BaseFilterOption>
          ).selected;
          break;
        case 'rangeSelection':
          (newFilter as RangeSelection).selected = (
            oldFilter as RangeSelection
          ).selected;
          break;
        default: // There are no other types in dynamic platform filters currently
      }
    });

    draft.platformId = option.id as PlatformId;
  });
  setFilter(filtersGroupId, 'platformMetrics', updatedPlatformMetrics);
}

function handleAudienceFiltersRelevance(
  option: BaseFilterOption,
  filtersGroupId: FiltersGroupId,
  filterId: FilterIds
) {
  if (filtersGroupId !== 'platforms' || filterId !== 'platform') return;
  creatorCrmStore.setState((draft) => {
    const filters = draft.content.filtersState.filtersGroups.platforms
      .filters as WritableDraft<Record<PlatformFilterId, Filter>>;
    const isIrrelevant = option.id !== 'instagram';
    filters.age.isIrrelevant = isIrrelevant;
    filters.gender.isIrrelevant = isIrrelevant;
    filters.location.isIrrelevant = isIrrelevant;
  });
}

const updateFilterOption = (
  filterSelectionType: UpdatableFilterOptionSelection,
  filtersGroupId: FiltersGroupId,
  filterId: FilterIds,
  update: FilterUpdate,
  onlyIfDrawerOpen = true
) => {
  const {filtersState} = creatorCrmStore.getState().content;
  const filter = findExpandedFilter(
    filtersState,
    filterSelectionType,
    filtersGroupId,
    filterId,
    undefined,
    onlyIfDrawerOpen
  );
  if (typeof filter === 'undefined') {
    console.log('Invalid input - operation is not possible');
    return;
  }
  let updatedFilter;
  switch (update.updateType) {
    case 'lda':
      updatedFilter = updateLda(
        filterSelectionType,
        filter,
        update as LdaUpdate
      );
      break;
    case 'weight':
      updatedFilter = updateWeight(
        filterSelectionType,
        filter,
        update as WeightUpdate
      );
      break;
    default:
  }
  if (updatedFilter) {
    setFilter(filtersGroupId, filterId, updatedFilter);
  }
};

const setFiltersOptionsFromMetadata = (metadata: Metadata) => {
  creatorCrmStore.setState((draft) => {
    draft.isMetadataLoaded = true;
    const filterGroups = Object.values(
      draft.content.filtersState.filtersGroups
    );
    filterGroups.forEach((group) => {
      const filters = Object.values(group.filters);
      filters.forEach((filter) => {
        updateAsyncInitializableFilterOptions(filter, metadata);
      });
    });
  });
};

const setCampaignHistoryBrandContext = (brandName: string, orgName: string) => {
  const namesMatch = brandName === orgName;
  const brandSuffix = namesMatch ? ` ${translate('(Brand)')}` : '';
  const orgSuffix = namesMatch ? ` ${translate('(Org)')}` : '';

  creatorCrmStore.setState((draft) => {
    [
      draft.content.filtersState.filtersGroups,
      draft.content.filtersState.filtersGroupsSnapshot,
    ].forEach((groups) => {
      const filter = groups.creatorDetails.filters.campaignHistory;
      if (filter?.type !== 'singleSelection') return;
      const typed = filter as WritableDraft<SingleSelection<BaseFilterOption>>;
      [...typed.options, typed.selected].filter(Boolean).forEach((opt) => {
        opt!.translateParams = opt!.id.includes('brand')
          ? {brand_name: `${brandName}${brandSuffix}`}
          : {agency_name: `${orgName}${orgSuffix}`};
      });
    });
  });
};

function setFilter(
  filtersGroupId: FiltersGroupId,
  filterId: FilterIds,
  filter: Filter,
  platformFilterId?: PlatformFilterIds
) {
  creatorCrmStore.setState((draft) => {
    const filtersGroup =
      draft.content.filtersState.filtersGroups[filtersGroupId];

    if (platformFilterId) {
      const dynamicPlatformFilters = filtersGroup.filters[
        filterId
      ] as WritableDraft<DynamicPlatformFilters>;

      const platformFilters =
        dynamicPlatformFilters.platformsFilters[
          dynamicPlatformFilters.platformId
        ];
      platformFilters[platformFilterId] = filter;
    } else {
      filtersGroup.filters[filterId] = filter;
    }
  });
}

const initFromUrl = (newState: CreatorCrmStateDefinition) =>
  creatorCrmStore.setState(
    (prev) => ({...newState, isSelectMode: prev.isSelectMode}),
    true
  );

const setSelectedSegmentId = (segmentId: string | null) =>
  creatorCrmStore.setState((draft) => {
    draft.selectedSegmentId = segmentId;
  });

const setTotalResults = (totalResults: number) => {
  creatorCrmStore.setState((draft) => {
    draft.content.paging.totalResults = totalResults;
  });
};

function clearSelectionState(draft: CreatorCrmStateDefinition) {
  draft.content.selectedCreators = new Map();
  draft.content.isAllFilterMatchingSelected = false;
}

const toggleSelectMode = () =>
  creatorCrmStore.setState((draft) => {
    draft.isSelectMode = !draft.isSelectMode;
    if (!draft.isSelectMode) {
      clearSelectionState(draft);
    }
  });

const deactivateSelectMode = () =>
  creatorCrmStore.setState((draft) => {
    draft.isSelectMode = false;
    clearSelectionState(draft);
  });

const toggleCrmMode = () =>
  creatorCrmStore.setState((draft) => {
    draft.isCrmMode = !draft.isCrmMode;
    resetSessionInformation(draft.content);
  });

const changeTableColumnVisibility = (columnId: string, visible: boolean) =>
  creatorCrmStore.setState((draft) => {
    const col = draft.content.tableColumns.find((c) => c.id === columnId);
    if (col) {
      col.visible = visible;
      if (!visible) col.pinned = false;
    }
  });

const changeTableColumnPin = (columnId: string, pinned: boolean) =>
  creatorCrmStore.setState((draft) => {
    const col = draft.content.tableColumns.find((c) => c.id === columnId);
    if (col && !col.locked) col.pinned = pinned;
  });

export const creatorCrmActions = {
  initFromUrl,
  updateSearchType,
  updateSearchChips,
  changePage,
  setSessionInformation,
  changeViewType,
  selectCreator,
  toggleSelectAllCreatorsOnPage,
  setIsAllFilterMatchingSelected,
  clearSelectedCreators,
  changeSorting,
  // filters
  openFiltersDrawer,
  closeFiltersDrawer,
  resetFilters,
  resetFiltersAndCloseDrawer,
  resetAllFilters,
  applyFiltersGroup,
  applyFiltersFromFilterGroups,
  applyFiltersFromFilterGroupsAndResetSession,
  applyAppliedFiltersGroups,
  toggleExpandFilter,
  changeFilterOptionSelection,
  forceChangeFilterOptionSelection,
  forceChangeFilterRange,
  changeFilterRange,
  updateFilterOption,
  changeUserInputFilter,
  changeSingleUserInputFilter,
  setFiltersOptionsFromMetadata,
  setCampaignHistoryBrandContext,
  updateUserLastBrandId,
  setTotalResults,
  changeTableColumnVisibility,
  changeTableColumnPin,
  setSelectedSegmentId,
  toggleSelectMode,
  deactivateSelectMode,
  toggleCrmMode,
};

export const useCreatorCrmStore = creatorCrmStore;
export default creatorCrmStore;
